import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import {
  BiContextProvidedValue,
  BiData,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiClickLogger } from '@wix/da-bi/pkg/hooks/useBiClickLogger';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import GallectionListItemButton from '../../../GallectionListItem/GallectionListItemButton';
import StreamedList from '../../../StreamedList';
import { FolderType } from '../../redux/types';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import s from './FolderList.scss';

export interface Props {
  streamId: string;
  deviationId: number;
  groupId: number;
  submissionType: FolderType;
  folders: {
    folder: PapiGallection;
    loading: boolean;
    added: boolean;
    suggested: boolean;
  }[];
  isFetching: boolean;
  hasMore: boolean;
  hasOnlyOneChunkOfGroupFoldersToLoad: boolean;
  onFolderSelected: (folderId: number) => void;
}

const FolderList: React.FC<Props> = ({
  submissionType,
  folders,
  isFetching,
  streamId,
  hasOnlyOneChunkOfGroupFoldersToLoad,
  onFolderSelected,
}) => {
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState<string>('');

  const hasNoFolders = !isFetching && !folders.length;
  const [justAddedToFolderIds, setAddedToFolderIds] = useState<number[]>([]);

  const onGallectionListItemClick = ({ folderId }) => {
    yieldToMain(() => {
      onFolderSelected(folderId);
      setAddedToFolderIds([...justAddedToFolderIds, folderId]);
    });
  };

  const { setTrackingRef: setInputRef } = useBiClickLogger(
    BiData<DefaultBiEvent>({
      evid: 2,
      click_info:
        submissionType === FolderType.Collection
          ? 'addToGroup.searchCollection'
          : 'addToGroup.searchGallery',
      sectionname: BiContextProvidedValue,
      widgetname: BiContextProvidedValue,
      typeid: BiContextProvidedValue,
      itemid: BiContextProvidedValue,
    })
  );

  return (
    <>
      {hasOnlyOneChunkOfGroupFoldersToLoad && (
        <TextInput
          id="add-to-group-filter-input"
          ref={setInputRef}
          value={filterValue}
          className={s['filter-input']}
          onChange={ev => setFilterValue(ev.target.value)}
          placeholder={t('addToGroupPopup.input.filter.placeholder')}
          prefix={<SearchIcon />}
          label={t('addToGroupPopup.input.filter.placeholder')}
          isLabelHidden
          noBottomSpacing
        />
      )}
      <StreamedList
        streamId={streamId}
        className={s['list']}
        wrapperClassName={s['scrollable']}
      >
        {folders.map(({ folder, added, suggested }) => {
          if (
            !folder ||
            (filterValue &&
              !folder.name.toLowerCase().includes(filterValue.toLowerCase()))
          ) {
            return null;
          }

          const renderSuffix = () => {
            let buttonI18nKey = 'addToGroupPopup.button.add';
            if (added) {
              buttonI18nKey = 'addToGroupPopup.button.added';
            }
            if (suggested) {
              buttonI18nKey = 'addToGroupPopup.button.suggested';
            }
            return (
              <TextButton
                size="small"
                variant="approval-green"
                disabled={added || suggested}
              >
                {t(buttonI18nKey)}
              </TextButton>
            );
          };

          return (
            <GallectionListItemButton
              className={s['item']}
              key={folder.folderId}
              checked={
                added ||
                suggested ||
                justAddedToFolderIds.includes(folder.folderId)
              }
              disabled={added || suggested}
              gallection={folder}
              showSuffixOnlyOnHover={!added && !suggested}
              suffix={renderSuffix()}
              onClick={() => onGallectionListItemClick(folder)}
            />
          );
        })}
        {hasNoFolders && (
          <div className={s['empty']}>
            {t(`addToGroup.noFolders.${submissionType}`)}
          </div>
        )}
      </StreamedList>
      {isFetching && <LoadingIndicator view="llama" className={s['loader']} />}
    </>
  );
};
FolderList.displayName = 'FolderList';

export default FolderList;
